/// <reference types="react-scripts" />

export interface IdResponse {
  id: string;
}
export interface SuccessResponse {
  success: boolean;
}

export type ApiErrorResponse = {
  error: {
    code: string;
    description: string;
    additionalInfo: object;
  };
  request: {
    uri: string;
    method: string;
    queryString: string;
    body: object;
  };
};

export type ApiResponse<T> = {
  request: {
    uri: string;
    method: string;
    queryString: string;
    body: object;
  };
  data: T;
};

export interface SignUpResponse {
  validation: Validation;
  uuid?: string;
  waitlist: boolean;
  contactAdmin?: boolean;
}

export interface ValidationResponse {
  validation: Validation;
}

export interface VerifyOtpResponse {
  verified: boolean;
  reason: string;
}

export interface ResendOtpResponse {
  found: boolean;
}
export interface Violation {
  element: string;
  violation: string;
}

export interface Validation {
  violations: Violation[];
  valid: boolean;
}

export interface SubscriptionResponse {
  status: 'Requested' | 'Active' | 'Failed';
  url?: string;
  orgUrlStatus?: 'VERIFIED' | 'VERIFICATION_IN_PROGRESS' | 'TIMED_OUT';
  orgUrl?: string;
}

export interface MoneyDTO {
  amount: number;
  currency: string;
  precision: number;
}

export interface Threshold {
  from: number;
  to?: number;
}

export interface StripeIntent {
  client_secret: string;
  totals: number;
}

export interface RazorpayOrderCreated {
  orderId: string;
  amount: number | string;
}

export interface TaxBreakdown {
  label: string;
  description: string;
  amount: MoneyDTO;
  rate: number;
}

export interface PriceBreakdown {
  tierId: string;
  tierName: string;
  isTrial: boolean;
  label: string;
  description: string;
  productType: SignupProductType;
  originalPrice: MoneyDTO;
  proRatedPrice: MoneyDTO;
  proRatedPriceAfterDiscount: MoneyDTO;
  proRatedPriceDiscountAmount: MoneyDTO;
  prorateApplied: boolean;
  taxes: TaxBreakdown[];
}

export interface APIKeyProduct {
  solution: APISolution;
  key?: string;
  docs: { name: string; url: string }[];
}

export interface APISolution {
  associatedApis: Api;
  displayAttributes: {
    displayName: string;
  };
}

export interface Api {
  apiBaseUrl: string;
  apiDetails: ApiDetails[];
}

export interface ApiDetails {
  name: string;
  relativePath: string;
}

export interface CreateAccountResponse {
  payment: boolean;
  user: boolean;
  organization: boolean;
  assignedOwner: boolean;
  subscription: boolean;
  complete: boolean;
  organizationId?: string;
  userId?: string;
  requestId?: string;
  keys?: APIKeyProduct[];
}

export interface CompatibleAddonsResponse {
  selectedSolution: SolutionSummary;
  compatibleAddons: AddonSummary[];
  fullData: FullSolutionAddonData;
}

export interface SolutionSummary extends ProductSummary {
  promotionalPercent: number;
}

export interface FullSolutionSummary extends SolutionSummary {
  tier?: {
    tierName: string;
  };
}

export interface AddonSummary extends ProductSummary {
  product: string;
}

export interface SignupV2Request {
  type: 'SOLUTION' | 'APP';
  id: string;
  versionId: string;
  tierId: string;
  tierName: string;
}

export interface AddonPlanSummary {
  name: string;
  shortDescription: string;
  addonTiers: AddonTierSummary[];
}

export interface AddonTierSummary {
  id: string;
  label: string;
  cost: MoneyDTO;
}

export interface FullSolutionAddonData {
  selectedSolution: FullSolutionSummary;
}

export interface ProductSummary {
  name: string;
  cost: MoneyDTO;
  productCount: number;
  additionalProductCost: MoneyDTO;
}

export enum SignupProductType {
  SOLUTION = 'SOLUTION',
  APP = 'APP',
}

export enum SolutionType {
  SOLUTION_TYPE_UNSPECIFIED,
  APP,
  API,
}

export interface ProductFamily {
  name: string;
  solutions: SolutionPlan[];
}

export interface Plan {
  icons: string[];
  images: string[];
  displayName: string;
  longDescription: string;
  shortDescription: string;
  id: string;
  type: SignupProductType;
  solutionType: SolutionType;
  version: string;
}

export interface SolutionPlan extends Plan {
  addons: AddonPlan[];
  tiers: PlanTier[];
}

export interface AddonPlan extends Plan {
  tiers: PlanTier[];
}

export enum PricingPlanType {
  'BASE' = 'BASE',
  'BASE_USAGE' = 'BASE_USAGE',
  'USAGE_FLAT' = 'USAGE_FLAT',
  'USAGE_SLAB' = 'USAGE_SLAB',
  'BASE_USAGE_SLAB' = 'BASE_USAGE_SLAB',
}

export interface PlanTier {
  id: string;
  meta: PlanMetadata;
  label: string;
  pricing: PlanPricing;
  tierId: string;
  tierType: string;
  tierLevel: number;
  isTrial: boolean;
  pricingPlanType: PricingPlanType;
  components: Record<string, PlanPricing[]>;
}

export interface PlanMetadata {
  basePrice: string;
  basePriceV2: string;
  shortDescription: string;
  longDescription: string;
  analyticsCount: string;
  supportPhone: string;
  supportEmail: string;
  orderPrice: string;
  promotionalOrderCount: string;
  userCount: string;
  facilityCount: string;
  sku: string;
  productCount: number;
  billingFrequency: string;
  additionalSkuCost: string;
}

export interface PlanPricing {
  fromDate: string;
  toDate: string;
  name: string;
  originalPrice?: MoneyDTO;
  originalPriceAfterDiscount?: MoneyDTO;
  originalPriceDiscountAmount?: MoneyDTO;
  originalPriceDiscountPercentage?: number;
  prorateApplied?: boolean;
  proRatedPrice?: MoneyDTO;
  proRatedPriceAfterDiscount?: MoneyDTO;
  proRatedPriceDiscountAmount?: MoneyDTO;
  proRatedPriceDiscountPercentage?: number;
  dueBeforeTax?: MoneyDTO;
  threshold?: Threshold;
}
